/**
 * A fund's page over the whole window — opened from any fund name on the
 * Holdings tab (`docs/mockups/holdings-13f.html` §4, `docs/plans/holdings-13f.md`).
 *
 * One of the fifteen by its id (`berkshire`), any other 13F filer by its CIK:
 * the server answers both in one shape, at the fund's latest filed quarter.
 * Top to bottom: the header (book, positions, the quarter and its flows), the
 * largest positions, and that quarter's largest buys and sells. The search box
 * reaches any other filer by name.
 *
 * An overlay for ⤢ Expand's reasons — no popup to be refused, no window to
 * widen — rendered in place so the theme's `--qf-*` variables reach it.
 */
import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent as KeyEvent, type ReactElement, type ReactNode, type RefObject } from "react";
import {
  fetchFund, searchFunds, type FundHistory, type FundHit, type FundMove, type FundPage, type FundRow, type Holding,
} from "../api/holdings";
import {
  COLUMNS_UP_TO, type GroupBy, actionOf, nearestSector, returnTiles, sectorGroups, sectorKeys, squarify, topSeries, type TopMode,
} from "../lib/holdings-fund";
import { optionsHeavy, optionsNote, pct, quarterLabel, usd } from "../lib/holdings-view";

interface Props {
  fund: string;
  onClose: () => void;
  onFund: (fund: string) => void;
  /** A heatmap tile opens that stock's Holdings page; the fund it came from rides along for ← back. */
  onSymbol?: (symbol: string, from: { fund: string; name: string }) => void;
}

type Load = { state: "loading" } | { state: "none" } | { state: "ready"; data: FundPage };

export function FundOverlay({ fund, onClose, onFund, onSymbol }: Props): ReactElement {
  const [load, setLoad] = useState<Load>({ state: "loading" });
  useEffect(() => {
    let live = true;
    setLoad({ state: "loading" });
    void fetchFund(fund).then((data) => { if (live) setLoad(data ? { state: "ready", data } : { state: "none" }); });
    return () => { live = false; };
  }, [fund]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const title = load.state === "ready" ? load.data.fund.manager : fund;
  return (
    <div style={OVERLAY} role="dialog" aria-modal="true" aria-label={`${title} 13F portfolio`} data-testid="holdings-fund">
      <div style={BAR}>
        <span style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {title} <span style={MUTED}>· 13F portfolio</span>
        </span>
        <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <FundSearch onFund={onFund} />
          <button type="button" onClick={onClose} style={BTN} aria-label="Close">✕</button>
        </span>
      </div>
      <div style={BODY}>
        {load.state === "loading" && <div style={MUTED}>loading…</div>}
        {load.state === "none" && <div style={MUTED}>This fund's 13F is not available here yet.</div>}
        {load.state === "ready" && (
          <Page p={load.data} onSymbol={onSymbol && ((s) => onSymbol(s, { fund, name: load.data.fund.manager }))} />
        )}
      </div>
    </div>
  );
}

function Page({ p, onSymbol }: { p: FundPage; onSymbol?: (symbol: string) => void }): ReactElement {
  const q = p.period ? quarterLabel(p.period) : "—";
  const h = p.history;
  const [tmode, setTmode] = useState("act");
  const [tby, setTby] = useState<GroupBy>("sector");
  const [amode, setAmode] = useState("now");
  return (
    <>
      {optionsHeavy(p.fund.stockShare) && (
        <div style={WARN} data-testid="options-heavy">
          Mostly options: {optionsNote(p.fund.stockShare as number)}. Every "of book" below is a share of {pct(p.fund.stockShare)} of
          this filer&apos;s reported 13F.
        </div>
      )}
      {p.stale && p.period && (
        <div style={WARN}>Latest 13F is {q} — nothing filed for the newer quarter yet, or the fund has stopped filing.</div>
      )}
      <div style={TILES} data-testid="fund-tiles">
        {h && returnTiles(h).map((t) => (
          <Tile key={t.key} k={`${t.label} return`}
                v={<span style={t.value === null ? MUTED : t.value >= 0 ? POS : NEG}>{t.value === null ? "—" : ret(t.value)}</span>}
                s={t.value === null ? t.need : `SPY ${ret(t.spy)} · ${signedPts((t.value ?? 0) - (t.spy ?? 0))}`} />
        ))}
        <Tile k="13F value" v={usd(p.book)} s="stock book" />
        <Tile k="positions" v={p.positions.toLocaleString()} s={q} />
        <Tile k={`${q} net`} v={<span style={p.bought - p.sold >= 0 ? POS : NEG}>{usd(p.bought - p.sold, true)}</span>}
              s={`+${usd(p.bought)} / −${usd(p.sold)}`} />
        <Tile k="stock of its 13F" v={<span style={optionsHeavy(p.fund.stockShare) ? { color: "var(--qf-warn)" } : {}}>{pct(p.fund.stockShare)}</span>}
              s={p.period ? `quarter ended ${p.period}` : ""} />
      </div>

      <div style={GRID2}>
        <Card title="Portfolio — latest 13F"
              how={`Grouped by ${tby}, area = $ held, positions ≥ 0.5% of book. ${tmode === "act" ? "Green opened or added, red trimmed, grey held." : "Coloured by sector."}${onSymbol ? " Click a stock for who holds it." : ""}`}
              tool={<span style={{ display: "inline-flex", gap: 6 }}>
                <Seg options={[["sector", "Sector"], ["industry", "Industry"]]} value={tby} onChange={(v) => setTby(v as GroupBy)} />
                <Seg options={[["act", "What it did"], ["sec", "Sector colour"]]} value={tmode} onChange={setTmode} />
              </span>}>
          <Treemap rows={p.top} book={p.book} mode={tmode} by={tby} onSymbol={onSymbol} />
        </Card>
        <Card title="Sector exposure by quarter" how="Each sector's share of the stock book, one line each. ETFs and funds are their own line.">
          {h ? <SectorLines h={h} /> : <Empty />}
        </Card>
      </div>

      <Card title="Top 5 holdings over time"
            how="% of the stock book. A long history is an area; a few quarters are columns."
            tool={<Seg options={[["now", "Today's top 5, traced back"], ["each", "Each quarter's own top 5"]]} value={amode} onChange={setAmode} />}>
        {h ? <TopFive h={h} mode={amode as TopMode} /> : <Empty />}
      </Card>

      {h && h.moves.length > 0 && (
        <Card title={`What it did each quarter, ${h.moves[0]!.label} → ${h.moves[h.moves.length - 1]!.label}`}
              how="Each quarter against the one before, sized as Δ shares × that quarter-end's price.">
          <QuarterCards h={h} onSymbol={onSymbol} />
        </Card>
      )}

      <div style={GRID2}>
        <Card title={`Holdings · largest ${p.top.length} of ${p.positions.toLocaleString()}`}
              how="Sorted by $. Change = Δ shares × the quarter-end price — a stock that rose is not a buy.">
          <Holdings rows={p.top} q={q} onSymbol={onSymbol} />
        </Card>
        <Card title="Quarterly return vs SPY"
              how="Each quarter's 13F book held unchanged to the next quarter end — price only; no trades inside the quarter, nothing the 13F leaves out.">
          {h ? <QuarterReturns h={h} /> : <Empty />}
        </Card>
      </div>
    </>
  );
}

/** A return to one decimal, signed: +14.8%, −3.2%. */
function ret(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return "—";
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v * 100).toFixed(1)}%`;
}

function signedPts(d: number): string {
  return `${d >= 0 ? "+" : "−"}${Math.abs(d * 100).toFixed(1)} pts`;
}

function Card({ title, how, tool, children }: { title: string; how: string; tool?: ReactElement; children: ReactNode }): ReactElement {
  return (
    <section style={CARD}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontSize: 14 }}>{title}</h3>{tool}
      </div>
      <div style={{ ...MUTED, fontSize: 12, margin: "2px 0 8px" }}>{how}</div>
      {children}
    </section>
  );
}

function Seg({ options, value, onChange }: { options: [string, string][]; value: string; onChange: (v: string) => void }): ReactElement {
  return (
    <span style={SEG}>
      {options.map(([k, label]) => (
        <button key={k} type="button" aria-pressed={value === k} onClick={() => onChange(k)}
                style={{ ...SEGBTN, ...(value === k ? { background: "var(--qf-surface-alt)", color: "var(--qf-text)" } : {}) }}>
          {label}
        </button>
      ))}
    </span>
  );
}

function Empty(): ReactElement {
  return <div style={MUTED}>No history for this fund here yet.</div>;
}

function Tile({ k, v, s }: { k: string; v: ReactElement | string; s?: string }): ReactElement {
  return (
    <div style={TILE}>
      <div style={{ ...MUTED, fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}>{k}</div>
      <div style={{ fontFamily: MONO, fontSize: 18, fontWeight: 600 }}>{v}</div>
      {s && <div style={{ ...MUTED, fontFamily: MONO, fontSize: 11.5 }}>{s}</div>}
    </div>
  );
}

// ── the heatmap ─────────────────────────────────────────────────────────────
function Treemap({ rows, book, mode, by, onSymbol }: {
  rows: FundRow[]; book: number; mode: string; by: GroupBy; onSymbol?: (symbol: string) => void;
}): ReactElement {
  const [ref, W] = useWidth();
  const H = 320, PAD = 16;
  const groups = sectorGroups(rows, undefined, by);
  return (
    <div ref={ref} style={{ position: "relative", height: H }} data-testid="fund-treemap">
      {W > 0 && squarify(groups, { x: 0, y: 0, w: W, h: H }).map((g) => (
        <div key={g.sector} style={{ position: "absolute", left: g.box.x, top: g.box.y, width: g.box.w, height: g.box.h, border: "1px solid var(--qf-background)" }}>
          {squarify(g.rows.map((r) => ({ ...r, v: r.value })), { x: 0, y: PAD, w: g.box.w, h: Math.max(1, g.box.h - PAD) }).map((c) => {
            const fs = Math.max(9, Math.min(22, Math.sqrt(c.box.w * c.box.h) / 5));
            const act = actionOf(c);
            return (
              <div key={c.symbol} title={`${c.symbol} · ${c.name ?? ""} · ${usd(c.value)} · ${pct(c.pctBook)} of book · ${act}${onSymbol ? " — open its holders" : ""}`}
                   {...(onSymbol && {
                     role: "button", tabIndex: 0, "data-symbol": c.symbol,
                     onClick: () => onSymbol(c.symbol),
                     onKeyDown: (e: KeyEvent<HTMLDivElement>) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSymbol(c.symbol); } },
                   })}
                   style={{ cursor: onSymbol ? "pointer" : undefined, position: "absolute", left: c.box.x, top: c.box.y, width: c.box.w, height: c.box.h, display: "flex",
                            flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden",
                            border: "1px solid var(--qf-background)", color: "#fff", fontFamily: MONO, fontWeight: 600, fontSize: fs,
                            background: mode === "act" ? ACT[act] : sectorColor(c.sector) }}>
                {c.box.w > 30 && c.box.h > 16 ? c.symbol : ""}
                {c.box.w > 60 && c.box.h > 38 && <small style={{ fontWeight: 400, fontSize: Math.max(9, fs * 0.55) }}>{pct(c.pctBook)}</small>}
              </div>
            );
          })}
          <span style={GLABEL} title={`${g.sector} · ${pct(g.v / (book || 1))}`}>{g.sector} · {pct(g.v / (book || 1))}</span>
        </div>
      ))}
    </div>
  );
}

// ── sector lines ────────────────────────────────────────────────────────────
function SectorLines({ h }: { h: FundHistory }): ReactElement {
  const [ref, W] = useWidth();
  const H = 300, L = 38, B = 20, RIGHT = 150;
  const periods = Object.keys(h.sectors).sort();
  const keys = sectorKeys(h);
  const [hover, setHover] = useState<{ k: string; x: number; y: number; i: number } | null>(null);
  const max = Math.max(0.1, ...periods.flatMap((p) => Object.values(h.sectors[p] ?? {})));
  const yMax = Math.min(1, Math.ceil(max * 10) / 10);
  const X = (i: number): number => L + (W - L - RIGHT) * (periods.length === 1 ? 0.5 : i / (periods.length - 1));
  const Y = (v: number): number => H - B - ((H - B - 8) * v) / yMax;
  const last = periods[periods.length - 1] ?? "";
  let prevY = -99;
  const ends = keys.map((k) => ({ k, y: Y(h.sectors[last]?.[k] ?? 0), v: h.sectors[last]?.[k] ?? 0 })).sort((a, b) => a.y - b.y)
    .map((e) => { const y = Math.max(e.y, prevY + 12); prevY = y; return { ...e, y }; });
  return (
    <div ref={ref}>
      {W > 0 && (
        <svg width={W} height={H} data-testid="fund-sectors" onMouseLeave={() => setHover(null)}
             onMouseMove={(e) => {
               const r = e.currentTarget.getBoundingClientRect(), x = e.clientX - r.left, y = e.clientY - r.top;
               const span = W - L - RIGHT;
               const t = periods.length === 1 ? 0 : ((x - L) / span) * (periods.length - 1);
               const v = ((H - B - y) * yMax) / (H - B - 8);
               const k = x < L || x > W - RIGHT ? null : nearestSector(h, keys, t, v, (10 * yMax) / (H - B - 8));
               setHover(k ? { k, x, y, i: Math.max(0, Math.min(periods.length - 1, Math.round(t))) } : null);
             }}>
          <Axes W={W - RIGHT + 4} H={H} L={L} B={B} yMax={yMax} labels={periods.map(quarterLabel)} x={X} />
          {keys.map((k) => (
            <g key={k}>
              <polyline fill="none" stroke={sectorColor(k)} strokeWidth={hover?.k === k ? 3.5 : 2}
                        opacity={hover && hover.k !== k ? 0.3 : 1}
                        points={periods.map((p, i) => `${X(i)},${Y(h.sectors[p]?.[k] ?? 0)}`).join(" ")}><title>{k}</title></polyline>
              {periods.length <= COLUMNS_UP_TO && periods.map((p, i) => (
                <circle key={p} cx={X(i)} cy={Y(h.sectors[p]?.[k] ?? 0)} r={3} fill={sectorColor(k)}>
                  <title>{quarterLabel(p)} · {k} {pct(h.sectors[p]?.[k] ?? 0)}</title>
                </circle>
              ))}
            </g>
          ))}
          {hover && (() => {
            const p = periods[hover.i] ?? "", label = `${hover.k} · ${pct(h.sectors[p]?.[hover.k] ?? 0)} · ${quarterLabel(p)}`;
            const w = label.length * 6.6 + 12, x = Math.min(hover.x + 12, W - w - 2), y = Math.max(2, hover.y - 28);
            return (
              <g pointerEvents="none" data-testid="sector-hover">
                <rect x={x} y={y} width={w} height={20} rx={3} fill="var(--qf-surface)" stroke={sectorColor(hover.k)} />
                <text x={x + 6} y={y + 14} style={{ fill: "var(--qf-text)", font: `11px ${MONO}` }}>{label}</text>
              </g>
            );
          })()}
          {ends.filter((e) => e.v >= 0.005).map((e) => (
            <text key={e.k} x={W - RIGHT + 10} y={e.y + 4} style={{ fill: sectorColor(e.k), font: `11px ${MONO}` }}>
              {e.k.replace(" Services", "").replace("Consumer ", "Cons. ")} {Math.round(e.v * 100)}%
            </text>
          ))}
        </svg>
      )}
    </div>
  );
}

// ── top 5 over time ─────────────────────────────────────────────────────────
function TopFive({ h, mode }: { h: FundHistory; mode: TopMode }): ReactElement {
  const [ref, W] = useWidth();
  const H = 260, L = 38, B = 20;
  const periods = Object.keys(h.top).sort();
  const { names, value } = topSeries(h, mode);
  const totals = periods.map((p) => names.reduce((s, n) => s + value(p, n), 0));
  const yMax = Math.min(1, Math.max(0.1, Math.ceil(Math.max(0, ...totals) * 10) / 10));
  const Y = (v: number): number => H - B - ((H - B - 8) * v) / yMax;
  const columns = periods.length <= COLUMNS_UP_TO;
  const X = columns
    ? (i: number): number => L + ((W - L - 4) * (i + 0.5)) / periods.length
    : (i: number): number => L + (W - L - 4) * (periods.length === 1 ? 0.5 : i / (periods.length - 1));
  const shapes: ReactElement[] = [];
  let base = periods.map(() => 0);
  names.forEach((n, k) => {
    const top = periods.map((p, i) => base[i]! + value(p, n));
    const color = SERIES[k % SERIES.length];
    if (columns) {
      const bw = Math.min(90, ((W - L) / periods.length) * 0.5);
      periods.forEach((p, i) => {
        const v = value(p, n);
        if (v <= 0) return;
        const y0 = Y(base[i]!), y1 = Y(top[i]!);
        shapes.push(
          <g key={`${n}${p}`}>
            <rect x={X(i) - bw / 2} y={y1} width={bw} height={y0 - y1} fill={color} stroke="var(--qf-background)" strokeWidth={0.5}>
              <title>{quarterLabel(p)} · {n} {pct(v)}</title>
            </rect>
            {y0 - y1 > 14 && <text x={X(i)} y={(y0 + y1) / 2 + 4} textAnchor="middle" style={{ fill: "#fff", font: `11px ${MONO}` }}>{n} {pct(v)}</text>}
          </g>,
        );
      });
    } else {
      const pts = [...periods.map((_, i) => `${X(i)},${Y(top[i]!)}`), ...periods.map((_, i) => `${X(i)},${Y(base[i]!)}`).reverse()].join(" ");
      shapes.push(<polygon key={n} points={pts} fill={color} fillOpacity={0.85} stroke="var(--qf-background)" strokeWidth={0.5}><title>{n}</title></polygon>);
    }
    base = top;
  });
  return (
    <div ref={ref}>
      {W > 0 && (
        <svg width={W} height={H} data-testid="fund-top5">
          <Axes W={W} H={H} L={L} B={B} yMax={yMax} labels={periods.map(quarterLabel)} x={X} />
          {shapes}
        </svg>
      )}
      <div style={LEGEND}>
        {names.map((n, k) => <span key={n}><i style={{ ...SWATCH, background: SERIES[k % SERIES.length] }} />{n}</span>)}
      </div>
    </div>
  );
}

function Axes({ W, H, L, B, yMax, labels, x }: { W: number; H: number; L: number; B: number; yMax: number; labels: string[]; x: (i: number) => number }): ReactElement {
  const step = Math.max(1, Math.ceil(labels.length / Math.max(1, Math.floor((W - L) / 58))));
  return (
    <g style={{ font: `11px ${MONO}`, fill: "var(--qf-text-muted)" }}>
      {[0, 1, 2, 3, 4].map((t) => {
        const y = H - B - ((H - B - 8) * t) / 4;
        return (
          <g key={t}>
            <line x1={L} x2={W - 4} y1={y} y2={y} stroke="var(--qf-border)" strokeOpacity={0.6} />
            <text x={L - 6} y={y + 4} textAnchor="end">{Math.round((yMax * t * 100) / 4)}%</text>
          </g>
        );
      })}
      {labels.map((l, i) => ((i % step === 0 && labels.length - 1 - i >= step) || i === labels.length - 1) && (
        <text key={i} x={x(i)} y={H - 4} textAnchor="middle">{l}</text>
      ))}
    </g>
  );
}

// ── what it did each quarter ────────────────────────────────────────────────
function QuarterCards({ h, onSymbol }: { h: FundHistory; onSymbol?: (symbol: string) => void }): ReactElement {
  const byPeriod = new Map(h.quarters.map((q) => [q.period, q]));
  const max = Math.max(1, ...h.moves.map((m) => { const q = byPeriod.get(m.period); return (q?.bought ?? 0) + (q?.sold ?? 0); }));
  return (
    <div style={CARDS} data-testid="fund-quarters">
      {h.moves.map((m) => {
        const q = byPeriod.get(m.period);
        const bought = q?.bought ?? 0, sold = q?.sold ?? 0, net = bought - sold;
        return (
          <div key={m.period} style={QCARD}>
            <div style={{ display: "flex", justifyContent: "space-between", fontFamily: MONO, fontWeight: 600 }}>
              <span>{m.label}</span><span style={net >= 0 ? POS : NEG}>net {usd(net, true)}</span>
            </div>
            <div style={{ display: "flex", height: 9, margin: "7px 0 4px", background: "var(--qf-row-alt)" }}>
              <span style={{ width: `${(100 * bought) / max}%`, background: "var(--qf-positive)" }} />
              <span style={{ width: `${(100 * sold) / max}%`, background: "var(--qf-negative)" }} />
            </div>
            <div style={{ fontFamily: MONO, fontSize: 12 }}><span style={POS}>+{usd(bought)}</span> <span style={NEG}>−{usd(sold)}</span></div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, margin: "6px 0" }}>
              {(["new", "add", "trim", "exit"] as const).map((k) => (
                <span key={k} style={COUNT(k)}>{k.toUpperCase()} {q?.[k] ?? "—"}</span>
              ))}
            </div>
            <div style={{ ...MUTED, fontSize: 12, marginTop: 4 }}>Biggest buys</div>
            {m.buys.map((x) => <MoveRow key={x.symbol} m={x} onSymbol={onSymbol} />)}
            <div style={{ ...MUTED, fontSize: 12, marginTop: 6 }}>Biggest sells</div>
            {m.sells.map((x) => <MoveRow key={x.symbol} m={x} onSymbol={onSymbol} />)}
          </div>
        );
      })}
    </div>
  );
}

function MoveRow({ m, onSymbol }: { m: FundMove; onSymbol?: (symbol: string) => void }): ReactElement {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontFamily: MONO, fontSize: 12.5 }}>
      <span>
        {onSymbol
          ? <button type="button" style={{ ...SYM, ...LINK }} title={`${m.symbol} — open its holders`} onClick={() => onSymbol(m.symbol)}>{m.symbol}</button>
          : <b>{m.symbol}</b>}
        {m.status && <span style={{ ...TAG(m.status), marginLeft: 4 }}>{m.status}</span>}</span>
      <span style={m.trade > 0 ? POS : NEG}>{usd(m.trade, true)}</span>
    </div>
  );
}

// ── holdings and quarterly returns ──────────────────────────────────────────
function Holdings({ rows, q, onSymbol }: { rows: FundRow[]; q: string; onSymbol?: (symbol: string) => void }): ReactElement {
  const mx = Math.max(1e-9, ...rows.map((r) => r.pctBook ?? 0));
  return (
    <div style={{ maxHeight: 420, overflow: "auto" }}>
      <div style={{ ...HROW, ...HEADROW }}>
        <span>Stock</span><span>Company</span><span>Sector</span><span style={R}>of book</span><span style={R}>value</span><span style={R}>{q} change</span>
      </div>
      {rows.map((x) => (
        <div key={x.symbol} style={HROW}>
          {onSymbol
            ? <button type="button" style={{ ...SYM, ...LINK }} title={`${x.symbol} — open its holders`} onClick={() => onSymbol(x.symbol)}>{x.symbol}</button>
            : <span style={SYM}>{x.symbol}</span>}
          <span style={{ ...MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.name ?? ""}</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            <i style={{ ...SWATCH, background: sectorColor(x.sector) }} />{x.sector ?? "—"}
          </span>
          <span style={NUM}>
            <span style={{ display: "inline-block", height: 8, width: Math.max(2, (60 * (x.pctBook ?? 0)) / mx), background: "var(--qf-accent)", opacity: 0.75, borderRadius: 2, marginRight: 6 }} />
            {pct(x.pctBook)}
          </span>
          <span style={NUM}>{usd(x.value)}</span>
          <Activity x={x} />
        </div>
      ))}
    </div>
  );
}

function QuarterReturns({ h }: { h: FundHistory }): ReactElement {
  const [ref, W] = useWidth();
  const H = 180, L = 38, B = 20;
  const qs = h.quarters.filter((q) => q.ret !== null || q.spyRet !== null).slice(-12);
  if (!qs.length) return <Empty />;
  const m = Math.max(0.05, ...qs.flatMap((q) => [Math.abs(q.ret ?? 0), Math.abs(q.spyRet ?? 0)]));
  const Y0 = (H - B) / 2 + 4, sc = (H - B - 16) / 2 / m, bw = Math.max(3, (W - L) / qs.length / 3);
  return (
    <div ref={ref}>
      {W > 0 && (
        <svg width={W} height={H} data-testid="fund-returns" style={{ font: `11px ${MONO}`, fill: "var(--qf-text-muted)" }}>
          <line x1={L} x2={W - 4} y1={Y0} y2={Y0} stroke="var(--qf-border)" />
          <text x={L - 6} y={Y0 - sc * m + 4} textAnchor="end">+{Math.round(m * 100)}%</text>
          <text x={L - 6} y={Y0 + sc * m + 4} textAnchor="end">−{Math.round(m * 100)}%</text>
          {qs.map((q, i) => {
            const x = L + ((W - L - 4) * (i + 0.5)) / qs.length;
            const r = q.ret, s = q.spyRet;
            // the bar sits on the quarter it covers: a book at Q2's end held through Q3
            return (
              <g key={q.period}>
                {r !== null && <rect x={x - bw} y={r >= 0 ? Y0 - r * sc : Y0} width={bw} height={Math.abs(r) * sc}
                                      fill={r >= 0 ? "var(--qf-positive)" : "var(--qf-negative)"}><title>{q.label} book → next quarter end {ret(r)}</title></rect>}
                {s !== null && <rect x={x} y={s >= 0 ? Y0 - s * sc : Y0} width={bw} height={Math.abs(s) * sc} fill="var(--qf-text-muted)"><title>SPY {ret(s)}</title></rect>}
                <text x={x} y={H - 4} textAnchor="middle">{q.label}</text>
              </g>
            );
          })}
        </svg>
      )}
      <div style={LEGEND}><span><i style={{ ...SWATCH, background: "var(--qf-positive)" }} />book</span><span><i style={{ ...SWATCH, background: "var(--qf-text-muted)" }} />SPY</span></div>
    </div>
  );
}

function Activity({ x }: { x: Holding }): ReactElement {
  if (x.status === "NEW" || x.status === "EXIT") {
    return <span style={NUM}><span style={x.status === "EXIT" ? NEG : POS}>{usd(x.trade, true)}</span> <span style={TAG(x.status)}>{x.status}</span></span>;
  }
  if (x.trade === null || x.status === "HOLD" || x.status === null) return <span style={{ ...NUM, ...MUTED }}>—</span>;
  return <span style={{ ...NUM, ...(x.trade >= 0 ? POS : NEG) }}>{usd(x.trade, true)}</span>;
}

/** The element's width, kept current; 0 until measured, and in jsdom, which has no ResizeObserver. */
function useWidth(): [RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((e) => setW(e[0]?.contentRect.width ?? 0));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

/** Any 13F filer by name: the fifteen first, then everyone else, largest book first. */
function FundSearch({ onFund }: { onFund: (fund: string) => void }): ReactElement {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<FundHit[]>([]);
  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return; }
    let live = true;
    const t = setTimeout(() => { void searchFunds(q).then((h) => { if (live) setHits(h); }); }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q]);
  return (
    <span style={{ position: "relative" }}>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a fund…" aria-label="Find a fund"
             style={INPUT} />
      {hits.length > 0 && (
        <div style={MENU} role="listbox">
          {hits.map((h) => (
            <button key={h.id} type="button" role="option" aria-selected={false} style={HIT}
                    onClick={() => { setQ(""); setHits([]); onFund(h.id); }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {h.manager}{h.top15 && <span style={{ ...MUTED, marginLeft: 6 }}>Top 15</span>}
              </span>
              <span style={{ ...MUTED, fontFamily: MONO }}>{usd(h.value)}</span>
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

// ── styles (theme tokens only) ─────────────────────────────────────────────
const MONO = "ui-monospace, 'IBM Plex Mono', Consolas, monospace";
const MUTED: CSSProperties = { color: "var(--qf-text-muted)" };
const POS: CSSProperties = { color: "var(--qf-positive)" };
const NEG: CSSProperties = { color: "var(--qf-negative)" };
const OVERLAY: CSSProperties = {
  position: "fixed", inset: 0, zIndex: 1600, display: "flex", flexDirection: "column",
  background: "var(--qf-background)", color: "var(--qf-text)", fontSize: 14,
};
const BAR: CSSProperties = {
  flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
  padding: "8px 12px", borderBottom: "1px solid var(--qf-border)",
};
const BODY: CSSProperties = { flex: 1, minHeight: 0, overflow: "auto", padding: "10px 14px", display: "flex", flexDirection: "column", gap: 12 };
const BTN: CSSProperties = {
  padding: "2px 8px", fontSize: 12, cursor: "pointer", background: "transparent", color: "var(--qf-text)",
  border: "1px solid var(--qf-border)", borderRadius: 4,
};
const INPUT: CSSProperties = {
  width: 220, maxWidth: "45vw", padding: "3px 7px", fontSize: 13, background: "var(--qf-surface)", color: "var(--qf-text)",
  border: "1px solid var(--qf-border)", borderRadius: 4,
};
const MENU: CSSProperties = {
  position: "absolute", right: 0, top: "calc(100% + 4px)", width: 340, maxWidth: "90vw", maxHeight: 360, overflowY: "auto",
  background: "var(--qf-surface)", border: "1px solid var(--qf-border)", borderRadius: 5, zIndex: 1, boxShadow: "0 6px 18px rgba(0,0,0,.35)",
};
const HIT: CSSProperties = {
  display: "flex", justifyContent: "space-between", gap: 8, width: "100%", padding: "5px 9px", fontSize: 13,
  background: "transparent", color: "var(--qf-text)", border: 0, borderBottom: "1px solid var(--qf-border)", cursor: "pointer", textAlign: "left",
};
const WARN: CSSProperties = { border: "1px solid var(--qf-warn)", color: "var(--qf-warn)", borderRadius: 5, padding: "6px 10px", fontSize: 13 };
const TILES: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 };
const TILE: CSSProperties = { border: "1px solid var(--qf-border)", borderRadius: 5, padding: "6px 10px", background: "var(--qf-surface)" };
const GRID2: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 12, alignItems: "start" };
const CARD: CSSProperties = { border: "1px solid var(--qf-border)", borderRadius: 6, padding: "10px 12px", background: "var(--qf-surface)", minWidth: 0 };
const SEG: CSSProperties = { display: "inline-flex", border: "1px solid var(--qf-border)", borderRadius: 5, overflow: "hidden" };
const SEGBTN: CSSProperties = { background: "transparent", border: 0, color: "var(--qf-text-secondary)", padding: "3px 9px", fontSize: 12, cursor: "pointer" };
const GLABEL: CSSProperties = {
  // sits on the page background in the strip above its cells, so the page's text colour, not white
  position: "absolute", left: 4, top: 1, fontSize: 11.5, fontWeight: 600, color: "var(--qf-text)", whiteSpace: "nowrap", overflow: "hidden", maxWidth: "calc(100% - 8px)",
};
const LEGEND: CSSProperties = { display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 6, fontSize: 12, color: "var(--qf-text-secondary)" };
const SWATCH: CSSProperties = { display: "inline-block", width: 9, height: 9, borderRadius: 2, marginRight: 4 };
const CARDS: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 10 };
const QCARD: CSSProperties = { border: "1px solid var(--qf-border)", borderRadius: 6, padding: "9px 11px", minWidth: 0 };
const HROW: CSSProperties = {
  display: "grid", gridTemplateColumns: "64px minmax(80px, 1.4fr) minmax(80px, 1fr) 96px 76px 96px", gap: 6, alignItems: "center",
  fontSize: 13, padding: "2px 0", borderBottom: "1px solid var(--qf-row-alt)",
};
const COUNT = (k: "new" | "add" | "trim" | "exit"): CSSProperties => ({
  fontFamily: MONO, fontSize: 11, fontWeight: 600, padding: "1px 5px", borderRadius: 3,
  color: k === "new" || k === "add" ? "var(--qf-positive)" : "var(--qf-negative)",
  border: k === "new" || k === "exit" ? `1px solid ${k === "new" ? "var(--qf-positive)" : "var(--qf-negative)"}` : "1px solid transparent",
});
/**
 * Sectors and the top-5 series are categorical identities, like the fund palette
 * on the Holdings tab: a theme has six series colours and there are a dozen
 * sectors. They carry no meaning beyond telling lines apart.
 */
const SECTORS: Record<string, string> = {
  "Technology": "#4f7cff", "Communication Services": "#26c6da", "Consumer Cyclical": "#ffb74d", "Consumer Defensive": "#9ccc65",
  "Financial Services": "#ef5350", "Healthcare": "#ab47bc", "Industrials": "#8d6e63", "Energy": "#ff7043", "Utilities": "#26a69a",
  "Real Estate": "#ec407a", "Basic Materials": "#d4e157", "ETF / fund": "#78909c", "Other": "#546e7a",
};
const sectorColor = (s: string | null | undefined): string => SECTORS[s ?? "Other"] ?? "#546e7a";
const SERIES = ["#4f7cff", "#ffb74d", "#ef5350", "#26c6da", "#ab47bc", "#9ccc65", "#ff7043", "#ec407a", "#26a69a", "#d4e157", "#8d6e63", "#78909c"];
/** The heatmap's action colours: opened, added, held, trimmed. */
const ACT: Record<string, string> = { NEW: "#1b8a5a", ADD: "#2e7d32", HOLD: "#3d4a5a", TRIM: "#8e2b2b" };
const HEADROW: CSSProperties = { fontSize: 12, color: "var(--qf-text-muted)" };
const R: CSSProperties = { textAlign: "right" };
const SYM: CSSProperties = { fontFamily: MONO, fontWeight: 600 };
const LINK: CSSProperties = {
  padding: 0, border: 0, background: "transparent", color: "inherit", fontSize: "inherit", textAlign: "left", cursor: "pointer",
  textDecoration: "underline dotted", textUnderlineOffset: 3,
};
const NUM: CSSProperties = { fontFamily: MONO, fontSize: 12.5, textAlign: "right", whiteSpace: "nowrap" };
const TAG = (s: string): CSSProperties => ({
  fontFamily: MONO, fontSize: 10.5, fontWeight: 600, padding: "0 3px", borderRadius: 3,
  color: s === "EXIT" ? "var(--qf-negative)" : "var(--qf-positive)", border: `1px solid ${s === "EXIT" ? "var(--qf-negative)" : "var(--qf-positive)"}`,
});
